import { useMemo } from "react";
import Modal from "react-bootstrap/Modal";
import { formatCurrency } from "../../../Utils/helpers";

export default function ProcessPayoutModal({
  show,
  onHide,
  selectedDrivers,
  scheduleLabel,
  isProcessing,
  onConfirm,
}) {
  const totalAmount = selectedDrivers.reduce((sum, d) => sum + d.walletBalance, 0);
  const blockedDrivers = useMemo(
    () => selectedDrivers.filter((d) => d.payoutBlocked),
    [selectedDrivers]
  );
  const canProcess = selectedDrivers.length > 0 && blockedDrivers.length === 0;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop={isProcessing ? "static" : true}
      keyboard={!isProcessing}
      className="premium-cms-modal dp-payout-modal"
    >
      <Modal.Header className="cms-modal__header">
        <div className="cms-modal__header-main">
          <div className="cms-modal__header-icon">
            <i className="fas fa-wallet" />
          </div>
          <div>
            <Modal.Title>Confirm Driver Payout</Modal.Title>
            <p className="cms-modal__subtitle">
              Review selected drivers before transferring wallet balance to their bank accounts.
            </p>
          </div>
        </div>
        {!isProcessing && (
          <button type="button" className="btn-close" aria-label="Close" onClick={onHide} />
        )}
      </Modal.Header>

      <Modal.Body className="cms-modal__body">
        <div className="dp-confirm-summary">
          <div className="dp-confirm-summary__item">
            <span className="dp-confirm-summary__label">Payout Schedule</span>
            <strong>{scheduleLabel}</strong>
          </div>
          <div className="dp-confirm-summary__item">
            <span className="dp-confirm-summary__label">Drivers Selected</span>
            <strong>{selectedDrivers.length}</strong>
          </div>
          <div className="dp-confirm-summary__item dp-confirm-summary__item--highlight">
            <span className="dp-confirm-summary__label">Total Amount</span>
            <strong>{formatCurrency(totalAmount)}</strong>
          </div>
        </div>

        <div className="dp-confirm-drivers">
          <h6 className="dp-confirm-drivers__title">Selected Drivers</h6>
          <ul className="dp-confirm-drivers__list">
            {selectedDrivers.map((driver) => (
              <li key={driver.id} className="dp-confirm-drivers__row">
                <div className="dp-confirm-drivers__info">
                  <span className="dp-confirm-drivers__name">{driver.name}</span>
                  <span className="dp-confirm-drivers__bank">
                    {driver.bankLast4
                      ? `${driver.bankName} •••• ${driver.bankLast4}`
                      : "No bank account"}
                  </span>
                </div>
                <span className="dp-confirm-drivers__amount">
                  {formatCurrency(driver.walletBalance)}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {blockedDrivers.length > 0 && (
          <div className="dp-confirm-warning">
            <i className="fas fa-exclamation-triangle" />
            <div>
              <strong>Payout blocked</strong>
              <p>
                {blockedDrivers.length} selected driver(s) do not have a linked and verified bank
                account. Remove them from the selection to continue.
              </p>
            </div>
          </div>
        )}

        <p className="dp-confirm-note">
          <i className="fas fa-info-circle" />
          Each driver's full wallet balance is transferred via Stripe Connect. If a transfer fails,
          that driver's balance stays in their wallet.
        </p>
      </Modal.Body>

      <Modal.Footer className="cms-modal-footer">
        <button
          type="button"
          className="cms-btn-cancel"
          onClick={onHide}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button
          type="button"
          className="cms-btn-save"
          onClick={onConfirm}
          disabled={isProcessing || !canProcess}
        >
          {isProcessing ? (
            <>
              <span className="dp-spinner" />
              Processing…
            </>
          ) : (
            <>
              <i className="fas fa-paper-plane" />
              Process Payout
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
